import { useCallback, useMemo, useRef } from 'react';
import { useModals, ModalNames } from 'components/modals';
import useUI from 'hooks/use-ui';
import useUser from 'hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { InterfaceVariants } from 'src/constants/interface-variants';
import { usePhoneVerification } from 'src/components/modals/phone-verification-nudge/use-phone-verification';
import { deriveNudgeLeg } from 'src/components/modals/phone-verification-nudge/phone-verification.types';
import type {
  NudgeOutcome,
  PhoneVerificationNudgeContext,
} from 'src/components/modals/phone-verification-nudge/phone-verification.types';
import { usePhoneVerificationNudgeOperational } from './use-phone-verification-nudge-operational';
import { usePhoneVerificationNudgeAnalytics } from './use-phone-verification-nudge-analytics';
import { useSwitchToOtherAccount } from './use-switch-to-other-account';

// Deliberately narrower than use-post-signup-modal-sequence's EXCLUDED_VARIANTS:
// `embedded` is NOT excluded here -- the embedded iframe's Google/Apple popup + postMessage
// login still round-trips through the same orchestrator.
//
// `mobileEcomm` IS excluded. The nudge is structurally unreachable on that surface:
// ecomm-mobile has no native login, so AuthenticationButton.authenticate opens an ephemeral
// ASWebAuthenticationSession pointed at /mobile-ecomm/{cName}/login?redirectUrl={scheme}://...
// Two failure modes follow from that: (1) LoginModalContent doesn't forward redirectUrl to
// LoginContent, so handleLoginSuccess takes the non-redirect branch and schedules the nudge
// while LoginModalContent's own effect navigates to the custom-scheme callback as soon as the
// redirect URL resolves -- the OS intercepts the scheme and tears down the auth sheet before the
// nudge modal can ever complete; and (2) setNudgeIntentMarker writes to the sessionStorage of
// that same ephemeral session, which dies with the sheet, and the redirect target is a
// custom-scheme URL consumed natively, so there's never a marketplace page load to read the
// marker back. Attempting the nudge here only wastes a phoneVerificationNudgeStatus round-trip and
// risks a modal flash inside a sheet that's about to be destroyed. The native ecomm-mobile app
// implements its own bespoke nudge instead (see AuthenticationButton.authenticate in
// dispensary/lib/src/account/ui/widgets/authentication_button.dart); this hook's job ends at the
// web surface. Only kiosk and plus -- surfaces with no concept of an individual login-owned
// phone number -- round out the exclusion list alongside mobileEcomm.
const EXCLUDED_VARIANTS: InterfaceVariants[] = [
  InterfaceVariants.kiosk,
  InterfaceVariants.plus,
  InterfaceVariants.mobileEcomm,
];

export type TriggerPhoneVerificationNudgeParams = { viaMarker?: boolean };

export type UsePhoneVerificationNudgeResult = {
  triggerPhoneVerificationNudge: (params?: TriggerPhoneVerificationNudgeParams) => Promise<void>;
};

/**
 * Post-login orchestrator for the phone-verification "Nudge" modal. Modelled line-for-line on
 * `use-post-signup-modal-sequence.ts`: same re-entrancy-ref guard, same try/finally shape, same
 * `useCallback`/`useMemo` return contract. Fetches `phoneVerificationNudgeStatus`, derives which
 * leg (if any) to show via `deriveNudgeLeg`, opens the modal, and handles its terminal outcome.
 *
 * Every failure mode here is silent by design -- a status-fetch error, an already-active modal,
 * or a suppressed surface must never produce a toast, a console entry, or any other user-visible
 * artifact. The nudge has zero persistence: skipping a landing simply means the next login
 * re-runs this from scratch.
 */
export function usePhoneVerificationNudge(): UsePhoneVerificationNudgeResult {
  const Modals = useModals();
  const { openModal } = Modals;
  const UI = useUI();
  const User = useUser();
  const { dispensary } = useDispensary();
  const { fetchNudgeStatus } = usePhoneVerification();
  const isNudgeInFlightRef = useRef(false);
  const dispensaryId = dispensary?.id ?? null;
  // FINTECH-4632: always dispensary-derived, never present without a dispensaryId alongside it --
  // this is caller-supplied LD targeting context only, not a new authorization surface.
  const enterpriseId = dispensary?.retailer?.enterpriseId ?? null;
  const nudgeOperational = usePhoneVerificationNudgeOperational();
  const { trackSuppressed } = usePhoneVerificationNudgeAnalytics({ surface: 'login' });

  const handleLoginToOtherAccount = useSwitchToOtherAccount();

  const triggerPhoneVerificationNudge = useCallback(
    async (_params: TriggerPhoneVerificationNudgeParams = {}): Promise<void> => {
      // Every guard below (including the in-flight/active-modal checks) now lives INSIDE the
      // try: `triggerPhoneVerificationNudge` is called as bare `void trigger()` at every call
      // site (no `.catch`), so a synchronous throw from a guard -- e.g. an unexpectedly
      // undefined `User`/`UI`/`Modals` -- must be swallowed by the same silent `catch` below
      // rather than escaping as an unhandled promise rejection. The single `finally` covers
      // every exit path (guard bail, thrown error, or a normal completion) so
      // `isNudgeInFlightRef` can never be left stuck `true` by a path that skips resetting it.
      try {
        // 1. Magic Login claim continuation slots in HERE, ahead of the nudge.
        //    if (await runClaimContinuation()) return;

        // 2. Nudge -- suppression gate. Bails are evaluated, and silent, in exactly this order:
        //    (1) no session, (2) LD killswitch (ecomm.phone-identity.operational -- the same
        //    circuit breaker Armageddon's PhoneNumbers::VerificationGate checks server-side;
        //    it only ever avoids the dedicated Nudge status request, it does not make any rollout
        //    decision -- that's Armageddon's job, targeted per-user), (3) checkout page,
        //    (4) excluded surface, (5) already in flight, (6) a modal is already active (cheap
        //    pre-check; re-checked again below, immediately before opening -- see there).
        // Bails 1-2 (not_logged_in / not_operational) are deliberately NOT tracked -- bail 1
        // fires on every logged-out invocation and bail 2 fires for the entire population the
        // feature is dark for, so emitting either would swamp the table with uninformative rows.
        // Everything from bail 3 onward is a genuinely interesting "we would have nudged but
        // didn't," and is tracked below.
        if (!User.isLoggedIn) {
          return;
        }
        if (!nudgeOperational) {
          return;
        }
        if (UI.isCheckoutPage) {
          trackSuppressed('checkout_page');
          return;
        }
        if (EXCLUDED_VARIANTS.includes(UI.variant)) {
          trackSuppressed('excluded_variant');
          return;
        }
        if (isNudgeInFlightRef.current) {
          trackSuppressed('in_flight');
          return;
        }
        // (5) Modal collision (cheap short-circuit): openModal() never resolves for a name that's
        // already active (it just console.errors and returns), so an already-open modal of any
        // name must skip this landing entirely rather than risk hanging forever. There's no
        // persistence to lose by skipping -- the next login re-runs status from scratch. This is
        // only a pre-check: `fetchNudgeStatus` below is a network round-trip, during which an unrelated
        // modal (age gate, order-type takeover, announcement) can open, so we re-check immediately
        // before `openModal` as well -- see below.
        if (Modals.activeModals.size > 0) {
          trackSuppressed('modal_collision');
          return;
        }

        isNudgeInFlightRef.current = true;

        let statusResult: Awaited<ReturnType<typeof fetchNudgeStatus>>;

        try {
          statusResult = await fetchNudgeStatus(dispensaryId, enterpriseId);
        } catch {
          // Silent -- a failed status fetch must never surface. Scoped to ONLY this call so a
          // later failure (openModal, handleLoginToOtherAccount) is not misattributed to a
          // status-fetch problem -- see the outer catch below.
          trackSuppressed('status_fetch_failed');
          return;
        }

        const { phoneVerificationNudgeStatus } = statusResult;
        const leg = deriveNudgeLeg(phoneVerificationNudgeStatus?.status, phoneVerificationNudgeStatus?.hasPhone);

        if (leg === 'none') {
          trackSuppressed('leg_none');
          return;
        }

        // Re-check for a modal collision immediately before opening: the check above ran before
        // `fetchNudgeStatus`'s network round-trip, during which some unrelated modal (age gate,
        // order-type takeover, announcement) may have opened. Bail silently rather than stacking
        // the nudge on top of it -- same zero-persistence reasoning as the early check.
        if (Modals.activeModals.size > 0) {
          trackSuppressed('modal_collision');
          return;
        }

        const context: PhoneVerificationNudgeContext = {
          initialLeg: leg,
          ownerEmailHint: phoneVerificationNudgeStatus?.ownerEmailHint ?? null,
          dispensaryId,
          enterpriseId,
        };

        const outcome = await openModal<PhoneVerificationNudgeContext, NudgeOutcome>(
          ModalNames.phoneVerificationNudge,
          context
        );

        if (outcome.kind === 'login_other_account') {
          await handleLoginToOtherAccount();
        }
      } catch {
        // Silent -- covers every guard/openModal/handleLoginToOtherAccount failure that isn't
        // the status fetch itself (that one is caught and labeled above, closer to its source).
        // Never surfaces to the user.
        trackSuppressed('unexpected_error');
      } finally {
        isNudgeInFlightRef.current = false;
      }
    },
    [
      User,
      UI,
      Modals,
      fetchNudgeStatus,
      dispensaryId,
      enterpriseId,
      nudgeOperational,
      openModal,
      handleLoginToOtherAccount,
      trackSuppressed,
    ]
  );

  return useMemo(() => ({ triggerPhoneVerificationNudge }), [triggerPhoneVerificationNudge]);
}
